import {
  Canvas,
  useFrame,
  useThree,
  type ThreeEvent,
} from "@react-three/fiber";
import { Component, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MutableRefObject, ReactNode } from "react";
import * as THREE from "three";
import {
  computeLayoutBounds,
  computeLayoutViewRadius,
  getDoorExitCell,
  getTileHeight,
  getWalkableCells,
  getWalkableSet,
  TILE_WORLD_HEIGHT_UNIT,
} from "../../shared/room-layout";
import { resolveFurnitureFootprintCellsForAsset } from "../../shared/furniture-footprint";
import { resolveSeatPoses } from "../../shared/seating";
import type {
  AvatarState,
  RoomLayoutRuntime,
  RoomStatePayload,
  RoomThemeRuntime,
} from "../../shared/types";
import type { RoomMotionFeed } from "../hooks/useRoomConnection";
import type { SelectedAsset } from "../hooks/useMarketplace";
import { CLEAR_COLOR } from "../lib/constants";
import { DrobbitAvatar } from "./DrobbitAvatar";
import { FrameAsset } from "./FrameAsset";
import { IsometricRoom } from "./IsometricRoom";
import { NormalizedGlb } from "./NormalizedGlb";
import { PlacementGhost } from "./PlacementGhost";
import { RoomChatBubbles } from "./RoomChatBubbles";

/** Catches GLB parse/load errors so one bad asset doesn't crash the whole scene. */
class AssetErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: unknown) {
    console.warn("[RoomCanvas] Asset failed to load:", error);
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

/** Animated ice crystal shown while a GLB asset loads. */
function IceLoadingCrystal({
  position,
}: {
  position?: [number, number, number];
}) {
  const groupRef = useRef<THREE.Group>(null);
  const elapsed = useRef(0);

  const material = useMemo(
    () =>
      new THREE.MeshPhysicalMaterial({
        color: 0xb0e0ff,
        transparent: true,
        opacity: 0.55,
        roughness: 0.1,
        metalness: 0.2,
        transmission: 0.6,
        thickness: 0.5,
        depthWrite: false,
      }),
    []
  );

  useFrame((_, delta) => {
    elapsed.current += delta;
    if (groupRef.current) {
      groupRef.current.rotation.y = elapsed.current * 0.8;
      const bob = Math.sin(elapsed.current * 2) * 0.06;
      groupRef.current.position.y = 0.4 + bob;
      const pulse = 0.9 + Math.sin(elapsed.current * 3) * 0.1;
      groupRef.current.scale.setScalar(pulse);
    }
  });

  return (
    <group ref={groupRef} position={position ?? [0, 0.4, 0]}>
      {/* Main crystal — octahedron */}
      <mesh material={material}>
        <octahedronGeometry args={[0.28, 0]} />
      </mesh>
      {/* Small orbiting shard */}
      <mesh position={[0.35, 0.1, 0]} material={material} scale={0.4}>
        <octahedronGeometry args={[0.2, 0]} />
      </mesh>
      <mesh position={[-0.2, -0.15, 0.3]} material={material} scale={0.3}>
        <octahedronGeometry args={[0.2, 0]} />
      </mesh>
    </group>
  );
}

export type PlacementPhase = "idle" | "placing" | "confirming";

interface Props {
  roomState: RoomStatePayload | null;
  stateSampleTimeMs: number | null;
  motionFeedRef: MutableRefObject<RoomMotionFeed>;
  selfId: string | null;
  layout: RoomLayoutRuntime;
  theme: RoomThemeRuntime;
  editMode: boolean;
  placementPhase: PlacementPhase;
  placingAsset: SelectedAsset | null;
  placementRotation: number;
  confirmedPos: { x: number; y: number; z: number; rotation?: number } | null;
  movingAssetId?: string | null;
  selectedAssetId: string | null;
  onSelectAsset: (id: string | null) => void;
  onSelectPlayer?: (avatar: AvatarState) => void;
  showSelfEmoteWheel?: boolean;
  onToggleSelfEmoteWheel?: () => void;
  onDanceEmote?: () => void;
  onWaveEmote?: () => void;
  onMove: (x: number, z: number, facing?: number) => void;
  onInteractAsset?: (assetId: string) => void;
  onLockPosition: (placement: {
    x: number;
    y: number;
    z: number;
    rotation?: number;
  }) => void;
  onCanvasReady: (canvas: HTMLCanvasElement) => void;
}

const ZOOM_MIN = 22;
const ZOOM_MAX = 150;
const HOVER_OUTLINE_SIZE = 0.98;
const HOVER_OUTLINE_THICKNESS = 0.082;
const HOVER_OUTLINE_COLOR = "#ffffff";
const HOVER_OUTLINE_OPACITY = 0.46;
const HOVER_OUTLINE_Y_OFFSET = 0.009;

function defaultZoomForRadius(viewRadius: number): number {
  return THREE.MathUtils.clamp(350 / viewRadius, ZOOM_MIN, ZOOM_MAX);
}

function IsometricCamera({
  viewRadius,
  resetToken,
}: {
  viewRadius: number;
  resetToken: string;
}) {
  const { camera, gl, size } = useThree();
  const panOffset = useRef(new THREE.Vector3(0, 0, 0));

  // Reset camera framing when room geometry changes.
  useEffect(() => {
    const cam = camera as THREE.OrthographicCamera;
    panOffset.current.set(0, 0, 0);
    const d = 50;
    cam.position.set(d, d, d);
    cam.lookAt(0, 0, 0);
    cam.zoom = defaultZoomForRadius(viewRadius);
    cam.near = 0.1;
    cam.far = 500;
    cam.updateProjectionMatrix();
  }, [camera, viewRadius, resetToken]);

  // Update projection on resize (R3F updates the frustum bounds automatically,
  // but we need to call updateProjectionMatrix to keep our zoom correct)
  useEffect(() => {
    const cam = camera as THREE.OrthographicCamera;
    cam.updateProjectionMatrix();
  }, [camera, size]);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const cam = camera as THREE.OrthographicCamera;
      cam.zoom = THREE.MathUtils.clamp(
        cam.zoom * (1 - e.deltaY * 0.001),
        ZOOM_MIN,
        ZOOM_MAX
      );
      cam.updateProjectionMatrix();
    };
    const dom = gl.domElement;
    dom.addEventListener("wheel", handleWheel, { passive: false });
    return () => dom.removeEventListener("wheel", handleWheel);
  }, [camera, gl]);

  // Middle/right-mouse drag panning
  useEffect(() => {
    const dom = gl.domElement;
    let dragging = false;
    let dragPointerId: number | null = null;
    let lastX = 0;
    let lastY = 0;
    const isPanButton = (button: number) => button === 1 || button === 2;

    const onDown = (e: PointerEvent) => {
      if (!isPanButton(e.button)) return;
      e.preventDefault();
      dragging = true;
      dragPointerId = e.pointerId;
      lastX = e.clientX;
      lastY = e.clientY;
      dom.setPointerCapture(e.pointerId);
    };
    const onMove = (e: PointerEvent) => {
      if (!dragging) return;
      if (dragPointerId != null && e.pointerId !== dragPointerId) return;
      const cam = camera as THREE.OrthographicCamera;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      lastX = e.clientX;
      lastY = e.clientY;

      // Convert pixel delta to world units at current zoom
      const pixelsPerUnit = cam.zoom;
      // In isometric view, screen X maps to world X-Z diagonal
      const worldDx = -dx / pixelsPerUnit;
      const worldDy = -dy / pixelsPerUnit;
      // Isometric axes: screen right = +X -Z, screen up = -X -Z +Y
      const panX = worldDx * 0.707 + worldDy * 0.707;
      const panZ = -worldDx * 0.707 + worldDy * 0.707;

      panOffset.current.x += panX;
      panOffset.current.z += panZ;

      const d = 50;
      cam.position.set(d + panOffset.current.x, d, d + panOffset.current.z);
      cam.lookAt(panOffset.current.x, 0, panOffset.current.z);
      cam.updateProjectionMatrix();
    };
    const onUp = (e: PointerEvent) => {
      if (!isPanButton(e.button)) return;
      if (dragPointerId != null && e.pointerId !== dragPointerId) return;
      dragging = false;
      dragPointerId = null;
      if (dom.hasPointerCapture(e.pointerId)) {
        dom.releasePointerCapture(e.pointerId);
      }
    };
    const onCancel = () => {
      dragging = false;
      dragPointerId = null;
    };
    const onContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    dom.addEventListener("pointerdown", onDown);
    dom.addEventListener("pointermove", onMove);
    dom.addEventListener("pointerup", onUp);
    dom.addEventListener("pointercancel", onCancel);
    dom.addEventListener("lostpointercapture", onCancel);
    dom.addEventListener("contextmenu", onContextMenu);
    return () => {
      dom.removeEventListener("pointerdown", onDown);
      dom.removeEventListener("pointermove", onMove);
      dom.removeEventListener("pointerup", onUp);
      dom.removeEventListener("pointercancel", onCancel);
      dom.removeEventListener("lostpointercapture", onCancel);
      dom.removeEventListener("contextmenu", onContextMenu);
    };
  }, [camera, gl]);

  return null;
}

function ClickPlane({
  layout,
  editMode,
  placementPhase,
  placingAsset,
  onMove,
  onLockPosition,
  onSelectAsset,
  onHoverTileChange,
}: {
  layout: RoomLayoutRuntime;
  editMode: boolean;
  placementPhase: PlacementPhase;
  placingAsset: SelectedAsset | null;
  onMove: (x: number, z: number, facing?: number) => void;
  onLockPosition: (placement: {
    x: number;
    y: number;
    z: number;
    rotation?: number;
  }) => void;
  onSelectAsset: (id: string | null) => void;
  onHoverTileChange?: (cell: { x: number; z: number } | null) => void;
}) {
  const walkableCells = useMemo(() => getWalkableCells(layout), [layout]);
  const walkableSet = useMemo(() => getWalkableSet(layout), [layout]);
  const [exitTileX, exitTileZ] = useMemo(() => getDoorExitCell(layout), [layout]);
  const exitTileY = useMemo(
    () =>
      getTileHeight(layout, layout.door.x, layout.door.z) * TILE_WORLD_HEIGHT_UNIT,
    [layout]
  );
  const bounds = useMemo(() => computeLayoutBounds(layout), [layout]);
  const planePadding = 2;
  const planeMinX = Math.min(bounds.minX, exitTileX) - planePadding;
  const planeMaxX = Math.max(bounds.maxX, exitTileX) + planePadding;
  const planeMinZ = Math.min(bounds.minZ, exitTileZ) - planePadding;
  const planeMaxZ = Math.max(bounds.maxZ, exitTileZ) + planePadding;
  const planeSizeX = planeMaxX - planeMinX + 1;
  const planeSizeZ = planeMaxZ - planeMinZ + 1;
  const planeCenterX = (planeMinX + planeMaxX) / 2;
  const planeCenterZ = (planeMinZ + planeMaxZ) / 2;

  const isWalkable = (x: number, z: number) => walkableSet.has(`${x},${z}`);

  const handleFloorTarget = (x: number, z: number, facing?: number) => {
    const isExitTile = x === exitTileX && z === exitTileZ;
    const isValidWalkTarget = isWalkable(x, z) || isExitTile;
    if (!isValidWalkTarget) {
      return;
    }
    if (placementPhase === "placing") {
      // Paintings are wall-only and are locked by the wall ghost logic.
      if (placingAsset?.type === "frame") {
        return;
      }
      // GLB placement lock is handled by PlacementGhost so stack-aware Y can be used.
      return;
    }
    if (placementPhase === "confirming") {
      // Ignore clicks on floor during confirm — use HUD/keyboard
      return;
    }
    // Deselect any selected asset when clicking empty floor in edit mode
    if (editMode) {
      onSelectAsset(null);
    }
    onMove(x, z, facing);
  };

  const onTilePointerDown = (event: ThreeEvent<PointerEvent>) => {
    // Only respond to left click
    if (event.nativeEvent.button !== 0) return;
    event.stopPropagation();
    const x = Number(event.object.userData.cellX);
    const z = Number(event.object.userData.cellZ);
    if (!Number.isFinite(x) || !Number.isFinite(z)) return;
    const dx = event.point.x - x;
    const dz = event.point.z - z;
    let facing: number | undefined;
    if (Math.abs(dx) > 0.05 || Math.abs(dz) > 0.05) {
      if (Math.abs(dx) > Math.abs(dz)) {
        facing = dx > 0 ? Math.PI / 2 : -Math.PI / 2;
      } else {
        facing = dz > 0 ? 0 : Math.PI;
      }
    }
    handleFloorTarget(x, z, facing);
  };

  const onTilePointerMove = (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation();
    const x = Number(event.object.userData.cellX);
    const z = Number(event.object.userData.cellZ);
    if (!Number.isFinite(x) || !Number.isFinite(z)) return;
    onHoverTileChange?.({ x, z });
  };

  const onExitTilePointerDown = (event: ThreeEvent<PointerEvent>) => {
    if (event.nativeEvent.button !== 0) return;
    event.stopPropagation();
    handleFloorTarget(exitTileX, exitTileZ);
  };

  const onExitTilePointerMove = (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation();
    onHoverTileChange?.({ x: exitTileX, z: exitTileZ });
  };

  const onBackgroundPointerDown = (event: ThreeEvent<PointerEvent>) => {
    if (event.nativeEvent.button !== 0) return;
    event.stopPropagation();
    if (editMode) {
      onSelectAsset(null);
    }
  };

  const onBackgroundPointerMove = (event: ThreeEvent<PointerEvent>) => {
    const x = Math.round(event.point.x);
    const z = Math.round(event.point.z);
    const isExitTile = x === exitTileX && z === exitTileZ;
    if (isWalkable(x, z) || isExitTile) {
      onHoverTileChange?.({ x, z });
      return;
    }
    onHoverTileChange?.(null);
  };

  useEffect(() => {
    return () => {
      onHoverTileChange?.(null);
    };
  }, [onHoverTileChange]);

  return (
    <>
      {walkableCells.map(([x, z]) => (
        <mesh
          key={`click-cell-${x}-${z}`}
          rotation={[-Math.PI / 2, 0, 0]}
          position={[x, getTileHeight(layout, x, z) * TILE_WORLD_HEIGHT_UNIT + 0.015, z]}
          onPointerDown={onTilePointerDown}
          onPointerMove={onTilePointerMove}
          onPointerOver={onTilePointerMove}
          userData={{ cellX: x, cellZ: z }}
        >
          <planeGeometry args={[1, 1]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
      ))}

      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[planeCenterX, -0.005, planeCenterZ]}
        onPointerDown={onBackgroundPointerDown}
        onPointerMove={onBackgroundPointerMove}
        onPointerOver={onBackgroundPointerMove}
      >
        <planeGeometry args={[planeSizeX, planeSizeZ]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      {/* Hidden click target for the exterior spawn/exit tile. */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[exitTileX, exitTileY + 0.016, exitTileZ]}
        onPointerDown={onExitTilePointerDown}
        onPointerMove={onExitTilePointerMove}
        onPointerOver={onExitTilePointerMove}
      >
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
    </>
  );
}

export function RoomCanvas({
  roomState,
  stateSampleTimeMs,
  motionFeedRef,
  selfId,
  layout,
  theme,
  editMode,
  placementPhase,
  placingAsset,
  placementRotation,
  confirmedPos,
  movingAssetId,
  selectedAssetId,
  onSelectAsset,
  onSelectPlayer,
  showSelfEmoteWheel = false,
  onToggleSelfEmoteWheel,
  onDanceEmote,
  onWaveEmote,
  onMove,
  onInteractAsset,
  onLockPosition,
  onCanvasReady,
}: Props) {
  const effectiveLayout = roomState?.layout ?? layout;
  const effectiveTheme = roomState?.theme ?? theme;
  const [hoverTile, setHoverTile] = useState<{ x: number; z: number } | null>(
    null
  );
  const handleHoverTileChange = useCallback(
    (cell: { x: number; z: number } | null) => {
      setHoverTile((previous) => {
        if (!cell && !previous) {
          return previous;
        }
        if (
          cell &&
          previous &&
          previous.x === cell.x &&
          previous.z === cell.z
        ) {
          return previous;
        }
        return cell;
      });
    },
    []
  );
  const [exitTileX, exitTileZ] = useMemo(
    () => getDoorExitCell(effectiveLayout),
    [effectiveLayout]
  );
  const hoverTopByCell = useMemo(() => {
    const map = new Map<string, number>();
    for (const asset of roomState?.assets ?? []) {
      if (asset.type !== "glb") {
        continue;
      }
      if (!asset.stackableTop && !asset.walkableTop) {
        continue;
      }
      const stackHeight = THREE.MathUtils.clamp(asset.stackHeight ?? 1, 0, 8);
      const assetY = asset.y ?? 0;
      for (const [cellX, cellZ] of resolveFurnitureFootprintCellsForAsset(asset)) {
        const key = `${cellX},${cellZ}`;
        const tileBaseY =
          getTileHeight(effectiveLayout, cellX, cellZ) * TILE_WORLD_HEIGHT_UNIT;
        const topY = tileBaseY + assetY + stackHeight;
        const previous = map.get(key);
        if (previous == null || topY > previous) {
          map.set(key, topY);
        }
      }
    }
    return map;
  }, [effectiveLayout, roomState?.assets]);
  const hoverTileY = useMemo(() => {
    if (!hoverTile) {
      return 0;
    }
    const key = `${hoverTile.x},${hoverTile.z}`;
    const stackedTop = hoverTopByCell.get(key);
    let baseY: number;
    if (hoverTile.x === exitTileX && hoverTile.z === exitTileZ) {
      baseY =
        getTileHeight(
          effectiveLayout,
          effectiveLayout.door.x,
          effectiveLayout.door.z
        ) * TILE_WORLD_HEIGHT_UNIT;
    } else {
      baseY =
        getTileHeight(effectiveLayout, hoverTile.x, hoverTile.z) *
        TILE_WORLD_HEIGHT_UNIT;
    }
    return Math.max(baseY, stackedTop ?? baseY) + HOVER_OUTLINE_Y_OFFSET;
  }, [effectiveLayout, exitTileX, exitTileZ, hoverTile, hoverTopByCell]);
  const sittingSeatMetadataByAssetId = useMemo(() => {
    const next = new Map<
      string,
      Array<{
        seatIndex: number;
        x: number;
        y: number;
        z: number;
        facing: number;
      }>
    >();
    for (const asset of roomState?.assets ?? []) {
      if (asset.type !== "glb" || !asset.sittable) {
        continue;
      }
      const seatPoses = resolveSeatPoses(asset, (seatCellX, seatCellZ) =>
        getTileHeight(effectiveLayout, seatCellX, seatCellZ) *
        TILE_WORLD_HEIGHT_UNIT
      );
      if (seatPoses.length === 0) {
        continue;
      }
      next.set(
        asset.id,
        seatPoses.map((seatPose) => ({
          seatIndex: seatPose.seatIndex,
          x: seatPose.x,
          y: seatPose.y,
          z: seatPose.z,
          facing: seatPose.facing,
        }))
      );
    }
    return next;
  }, [effectiveLayout, roomState?.assets]);

  useEffect(() => {
    setHoverTile(null);
  }, [effectiveLayout.id]);
  const cameraResetToken = useMemo(() => {
    let walkableCount = 0;
    let stairsCount = 0;
    let heightChecksum = 0;
    for (let row = 0; row < effectiveLayout.tiles.length; row += 1) {
      const tilesRow = effectiveLayout.tiles[row];
      for (let col = 0; col < tilesRow.length; col += 1) {
        const tile = tilesRow[col];
        if (!tile.walkable) continue;
        walkableCount += 1;
        if (tile.surface === "stairs") stairsCount += 1;
        heightChecksum += Math.round(tile.height * 2) * (row + 1) * (col + 1);
      }
    }
    return [
      effectiveLayout.id,
      effectiveLayout.width,
      effectiveLayout.height,
      effectiveLayout.door.x,
      effectiveLayout.door.z,
      effectiveLayout.door.direction,
      effectiveLayout.walls?.length ?? -1,
      walkableCount,
      stairsCount,
      heightChecksum,
    ].join(":");
  }, [effectiveLayout]);
  const viewRadius = useMemo(
    () => computeLayoutViewRadius(effectiveLayout),
    [effectiveLayout]
  );
  const avatars = useMemo(() => {
    if (!roomState) {
      return [];
    }
    return [...roomState.players, ...roomState.bots].map((avatar) => {
      if (!avatar.isSitting || !avatar.sittingAssetId) {
        return avatar;
      }
      const seatMetaList = sittingSeatMetadataByAssetId.get(
        avatar.sittingAssetId
      );
      const seatMeta =
        seatMetaList?.find(
          (slot) => slot.seatIndex === (avatar.sittingSeatIndex ?? 0)
        ) ?? seatMetaList?.[0];
      if (!seatMeta) {
        return avatar;
      }
      if (
        Math.abs(avatar.x - seatMeta.x) <= 0.0001 &&
        Math.abs(avatar.y - seatMeta.y) <= 0.0001 &&
        Math.abs(avatar.z - seatMeta.z) <= 0.0001 &&
        Math.abs(avatar.targetX - seatMeta.x) <= 0.0001 &&
        Math.abs(avatar.facing - seatMeta.facing) <= 0.0001 &&
        Math.abs(avatar.targetY - seatMeta.y) <= 0.0001 &&
        Math.abs(avatar.targetZ - seatMeta.z) <= 0.0001
      ) {
        return avatar;
      }
      return {
        ...avatar,
        x: seatMeta.x,
        y: seatMeta.y,
        z: seatMeta.z,
        targetX: seatMeta.x,
        targetY: seatMeta.y,
        targetZ: seatMeta.z,
        facing: seatMeta.facing,
        path: [],
      };
    });
  }, [roomState, sittingSeatMetadataByAssetId]);
  const sittingInteractionTypeByAssetId = useMemo(() => {
    const map = new Map<string, "sit" | "lay" | "door" | "teleport" | "none">();
    for (const asset of roomState?.assets ?? []) {
      if (asset.type !== "glb") continue;
      map.set(asset.id, asset.interactionType ?? "none");
    }
    return map;
  }, [roomState?.assets]);

  return (
    <Canvas
      shadows
      orthographic
      dpr={[1, 1.75]}
      camera={{
        position: [50, 50, 50],
        zoom: defaultZoomForRadius(viewRadius),
        near: 0.1,
        far: 500,
      }}
      onCreated={({ gl }) => {
        gl.setClearColor(CLEAR_COLOR);
        onCanvasReady(gl.domElement);
      }}
      style={{ width: "100%", height: "100%", display: "block" }}
    >
      <IsometricCamera viewRadius={viewRadius} resetToken={cameraResetToken} />

      <ambientLight intensity={0.5} color="#fff8f0" />
      <hemisphereLight intensity={0.4} color="#b8d8f0" groundColor="#e8d4b8" />
      <directionalLight
        position={[10, 18, 10]}
        intensity={1.2}
        color="#fff4e0"
        castShadow
        shadow-mapSize={[2048, 2048]}
        shadow-camera-left={-viewRadius - 2}
        shadow-camera-right={viewRadius + 2}
        shadow-camera-top={viewRadius + 2}
        shadow-camera-bottom={-viewRadius - 2}
        shadow-bias={-0.0005}
      />
      <directionalLight
        position={[5, 6, 12]}
        intensity={0.25}
        color="#d0e8ff"
      />
      <pointLight
        position={[-8, 3, -8]}
        intensity={0.15}
        color="#ffe8c0"
        distance={20}
      />

      <IsometricRoom layout={effectiveLayout} theme={effectiveTheme} />

      {hoverTile && placementPhase === "idle" && (
        <group
          rotation={[-Math.PI / 2, 0, 0]}
          position={[hoverTile.x, hoverTileY, hoverTile.z]}
        >
          <mesh position={[0, HOVER_OUTLINE_SIZE / 2 - HOVER_OUTLINE_THICKNESS / 2, 0]}>
            <planeGeometry args={[HOVER_OUTLINE_SIZE, HOVER_OUTLINE_THICKNESS]} />
            <meshBasicMaterial
              color={HOVER_OUTLINE_COLOR}
              transparent
              opacity={HOVER_OUTLINE_OPACITY}
              depthWrite={false}
              toneMapped={false}
            />
          </mesh>
          <mesh position={[0, -(HOVER_OUTLINE_SIZE / 2 - HOVER_OUTLINE_THICKNESS / 2), 0]}>
            <planeGeometry args={[HOVER_OUTLINE_SIZE, HOVER_OUTLINE_THICKNESS]} />
            <meshBasicMaterial
              color={HOVER_OUTLINE_COLOR}
              transparent
              opacity={HOVER_OUTLINE_OPACITY}
              depthWrite={false}
              toneMapped={false}
            />
          </mesh>
          <mesh position={[HOVER_OUTLINE_SIZE / 2 - HOVER_OUTLINE_THICKNESS / 2, 0, 0]}>
            <planeGeometry
              args={[
                HOVER_OUTLINE_THICKNESS,
                HOVER_OUTLINE_SIZE - HOVER_OUTLINE_THICKNESS * 2,
              ]}
            />
            <meshBasicMaterial
              color={HOVER_OUTLINE_COLOR}
              transparent
              opacity={HOVER_OUTLINE_OPACITY}
              depthWrite={false}
              toneMapped={false}
            />
          </mesh>
          <mesh position={[-(HOVER_OUTLINE_SIZE / 2 - HOVER_OUTLINE_THICKNESS / 2), 0, 0]}>
            <planeGeometry
              args={[
                HOVER_OUTLINE_THICKNESS,
                HOVER_OUTLINE_SIZE - HOVER_OUTLINE_THICKNESS * 2,
              ]}
            />
            <meshBasicMaterial
              color={HOVER_OUTLINE_COLOR}
              transparent
              opacity={HOVER_OUTLINE_OPACITY}
              depthWrite={false}
              toneMapped={false}
            />
          </mesh>
        </group>
      )}

      <ClickPlane
        layout={effectiveLayout}
        editMode={editMode}
        placementPhase={placementPhase}
        placingAsset={placingAsset}
        onMove={onMove}
        onLockPosition={onLockPosition}
        onSelectAsset={onSelectAsset}
        onHoverTileChange={handleHoverTileChange}
      />

      {(placementPhase === "placing" || placementPhase === "confirming") &&
        placingAsset && (
          <PlacementGhost
            layout={effectiveLayout}
            asset={placingAsset}
            placedAssets={roomState?.assets ?? []}
            rotation={placementRotation}
            phase={placementPhase}
            confirmedPos={confirmedPos}
            movingAssetId={movingAssetId}
            onLockPosition={onLockPosition}
          />
        )}

      {avatars.map((avatar) => (
        <DrobbitAvatar
          key={avatar.id}
          avatar={avatar}
          isSelf={avatar.id === selfId}
          isLaying={
            avatar.isSitting === true &&
            Boolean(avatar.sittingAssetId) &&
            sittingInteractionTypeByAssetId.get(avatar.sittingAssetId ?? "") ===
              "lay"
          }
          sampleTimeMs={stateSampleTimeMs}
          motionFeedRef={motionFeedRef}
          onSelect={onSelectPlayer}
          showChatBubbles={false}
          showEmoteWheel={avatar.id === selfId && showSelfEmoteWheel}
          onToggleEmoteWheel={
            avatar.id === selfId ? onToggleSelfEmoteWheel : undefined
          }
          onDanceEmote={avatar.id === selfId ? onDanceEmote : undefined}
          onWaveEmote={avatar.id === selfId ? onWaveEmote : undefined}
        />
      ))}

      <RoomChatBubbles
        roomId={roomState?.roomId ?? null}
        avatars={avatars}
        motionFeedRef={motionFeedRef}
      />

      {(roomState?.assets ?? []).map((asset) => (
        <AssetErrorBoundary key={asset.id}>
          <Suspense
            fallback={<IceLoadingCrystal position={[asset.x, 0, asset.z]} />}
          >
            {asset.type === "frame" ? (
              <FrameAsset
                asset={asset}
                layout={effectiveLayout}
                editMode={editMode}
                selected={asset.id === selectedAssetId}
                onSelect={() => onSelectAsset(asset.id)}
              />
            ) : (
              <NormalizedGlb
                asset={asset}
                layout={effectiveLayout}
                editMode={editMode}
                selected={asset.id === selectedAssetId}
                onSelect={() => onSelectAsset(asset.id)}
                onHoverCellChange={handleHoverTileChange}
                onInteract={
                  !editMode
                    ? (target) => {
                        if (asset.interactionType === "door") {
                          onInteractAsset?.(asset.id);
                          return;
                        }
                        if (
                          asset.sittable ||
                          asset.walkableTop ||
                          asset.interactionType === "teleport"
                        ) {
                          onMove(target.x, target.z, target.facing);
                        }
                      }
                    : undefined
                }
              />
            )}
          </Suspense>
        </AssetErrorBoundary>
      ))}
    </Canvas>
  );
}
